import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import "./GalleryTeaser.css";

// Import preview images
import img1 from "../asset/asset_psd/img_1.jpg";
import img2 from "../asset/asset_psd/img_2.jpg";
import img3 from "../asset/asset_psd/img_3.jpg";
import img4 from "../asset/asset_psd/img_4.jpg";
import img5 from "../asset/asset_psd/img_5.jpg";
import img6 from "../asset/asset_psd/img_6.jpg";
import img7 from "../asset/asset_psd/img_7.jpg";
import img8 from "../asset/asset_psd/img_8.jpg";

const GalleryTeaser = () => {
  const [ref, isVisible] = useScrollAnimation(0.1);
  const { scrollYProgress } = useScroll();
  const navigate = useNavigate();

  // Parallax effect for the section
  const y = useTransform(scrollYProgress, [0, 1], [0, -50]);

  const previewImages = [img1, img2, img3, img4, img5, img6, img7, img8];

  const handleImageClick = () => {
    navigate("/gallery");
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.8, y: 30 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <section className="gallery-teaser-section" ref={ref}>
      <motion.div className="gallery-teaser-container" style={{ y }}>
        <motion.h2
          className="gallery-teaser-title"
          variants={titleVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          Our Work
        </motion.h2>
        <motion.p
          className="gallery-teaser-subtitle"
          variants={titleVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          Explore our collection of bespoke creations
        </motion.p>

        <motion.div
          className="gallery-teaser-grid"
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          {previewImages.map((image, index) => (
            <motion.div
              key={index}
              className="gallery-teaser-item"
              variants={itemVariants}
              onClick={handleImageClick}
              style={{ cursor: "pointer" }}
              whileHover={{
                scale: 1.05,
                transition: { duration: 0.3 },
              }}
            >
              <motion.img
                src={image}
                alt={`Fashion design showcase ${index + 1}`}
                className="gallery-teaser-image"
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.5 }}
              />
              <motion.div
                className="gallery-teaser-overlay"
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <span>View Gallery</span>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="gallery-teaser-footer"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <Link to="/gallery" className="gallery-view-full">
            <span>View Full Gallery</span>
            <ArrowRight size={20} />
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default GalleryTeaser;
